import React, { useState, useCallback, useRef, useMemo, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, X, Download, HelpCircle, XCircle, Loader2, Clock, Info } from "lucide-react";
import { useToast } from "../ui/use-toast";
import Papa from "papaparse";
import { Separator } from "../ui/separator";
import { Badge } from "../ui/badge";
import { Checkbox } from "../ui/checkbox";
import XanoAPI from "../../lib/XanoAPI";

// Batch Import Constants
const MAX_IMPORT_BATCH = 10; // Process 10 clients per batch
const MAX_FILE_CLIENTS = 1000; // Maximum clients allowed in a single file
const BATCH_DELAY_MS = 500; // Delay between batches (prevents rate limiting)
const MAX_FILE_SIZE_MB = 5; // 5MB max file size
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

interface ImportClientsModalProps {
  trigger?: React.ReactNode;
  onSuccess?: () => void;
}

interface ParsedClient {
  id?: number | string;
  firstName: string;
  lastName: string;
  email?: string;
  phoneNumber?: string;
  dateOfBirth?: string;
  stateProvince?: string;
  type?: string;
  status?: string;
  lifecycleStage?: string;
  related_client_id?: number | string;
  // Snake_case versions (from CSV, will be normalized)
  first_name?: string;
  last_name?: string;
  phone_number?: string;
  date_of_birth?: string;
  state_province?: string;
  lifecycle_stage?: string;
  [key: string]: any;
}

interface ValidationError {
  row: number;
  field: string;
  message: string;
}

interface BatchInfo {
  totalBatches: number;
  currentBatch: number;
  batchProgress: number;
  batchSize: number;
}

interface ImportStats {
  total: number;
  processed: number;
  successful: number;
  created: number;
  updated: number;
  failed: number;
  startTime: number | null;
}

interface FailedClient {
  client: ParsedClient;
  error: string;
  batchNumber: number;
}

export function ImportClientsModal({
  trigger,
  onSuccess,
}: ImportClientsModalProps) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [parsedData, setParsedData] = useState<ParsedClient[]>([]);
  const [selectedIndices, setSelectedIndices] = useState<Set<number>>(new Set());
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [cancelImport, setCancelImport] = useState(false);
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0 });
  const [importComplete, setImportComplete] = useState(false);
  const [importResults, setImportResults] = useState({ success: 0, failed: 0 });
  const [isDragOver, setIsDragOver] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  
  // Batch processing state
  const [batchInfo, setBatchInfo] = useState<BatchInfo | null>(null);
  const [importStats, setImportStats] = useState<ImportStats>({
    total: 0,
    processed: 0,
    successful: 0,
    created: 0,
    updated: 0,
    failed: 0,
    startTime: null,
  });
  const [failedClients, setFailedClients] = useState<FailedClient[]>([]);
  const [showFailedClients, setShowFailedClients] = useState(false);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const api = XanoAPI.getInstance();

  // Prevent accidental tab/window closure during import
  useEffect(() => {
    // Only activate during active import (not before or after)
    const shouldPreventUnload = isImporting && !importComplete && importProgress.total > 0;
    
    if (shouldPreventUnload) {
      const handleBeforeUnload = (e: BeforeUnloadEvent) => {
        e.preventDefault();
        e.returnValue = "Import in progress. Are you sure you want to leave?";
        return "Import in progress. Are you sure you want to leave?";
      };

      window.addEventListener("beforeunload", handleBeforeUnload);
      
      // Ensure cleanup happens
      return () => {
        window.removeEventListener("beforeunload", handleBeforeUnload);
      };
    }
    
    // Explicit cleanup when conditions no longer met
    return () => {
      // No-op, but ensures cleanup
    };
  }, [isImporting, importComplete, importProgress.total]);

  // Validate and process file
  const validateAndProcessFile = useCallback((selectedFile: File) => {
    // Check file type
    if (!selectedFile.name.endsWith('.csv')) {
      toast({
        title: "Invalid File Type",
        description: "Please upload a CSV file (.csv extension required).",
        variant: "destructive",
      });
      return false;
    }

    // Check file size
    if (selectedFile.size > MAX_FILE_SIZE_BYTES) {
      toast({
        title: "File Too Large",
        description: `File size exceeds ${MAX_FILE_SIZE_MB}MB limit. Please upload a smaller file.`,
        variant: "destructive",
      });
      return false;
    }

    // Check if file is empty
    if (selectedFile.size === 0) {
      toast({
        title: "Empty File",
        description: "The uploaded file is empty. Please upload a valid CSV file.",
        variant: "destructive",
      });
      return false;
    }

    setFile(selectedFile);
    setImportComplete(false);
    parseFile(selectedFile);
    return true;
  }, [toast]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      validateAndProcessFile(selectedFile);
    }
  };

  // Drag and drop handlers
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) {
      validateAndProcessFile(droppedFile);
    }
  }, [validateAndProcessFile]);

  const parseFile = (file: File) => {
    setIsProcessing(true);
    setParsedData([]);
    setValidationErrors([]);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const rawData = results.data as ParsedClient[];
        
        // Check if CSV has data
        if (rawData.length === 0) {
          toast({
            title: "Empty CSV File",
            description: "The CSV file contains no data rows. Please add client data and try again.",
            variant: "destructive",
          });
          setIsProcessing(false);
          setFile(null);
          return;
        }

        const normalizedData = normalizeFieldNames(rawData);
        
        // Check file client limit
        if (normalizedData.length > MAX_FILE_CLIENTS) {
          toast({
            title: "File Too Large",
            description: `You can import up to ${MAX_FILE_CLIENTS} clients at once. Your file contains ${normalizedData.length} clients. Please split your file into smaller batches.`,
            variant: "destructive",
          });
          setIsProcessing(false);
          setFile(null);
          return;
        }
        
        setParsedData(normalizedData);
        const errors = validateData(normalizedData);
        
        // Auto-select all valid clients (those without errors)
        const validIndices = new Set<number>();
        normalizedData.forEach((_, index) => {
          const hasError = errors.some(err => err.row === index + 1);
          if (!hasError) {
            validIndices.add(index);
          }
        });
        setSelectedIndices(validIndices);
        
        setIsProcessing(false);

        // Show success toast for valid parsing
        if (errors.length === 0) {
          toast({
            title: "File Parsed Successfully",
            description: `Found ${normalizedData.length} valid client(s) ready to import.`,
          });
        } else {
          toast({
            title: "File Parsed with Warnings",
            description: `Found ${normalizedData.length} clients, but ${errors.length} validation error(s) detected.`,
            variant: "default",
          });
        }
      },
      error: (error) => {
        toast({
          title: "CSV Parse Error",
          description: `Failed to parse CSV file: ${error.message}. Please ensure the file format is correct.`,
          variant: "destructive",
        });
        setIsProcessing(false);
        setFile(null);
      },
    });
  };

  const validateData = (data: ParsedClient[]) => {
    const errors: ValidationError[] = [];
    const idSet = new Set<string | number>();

    data.forEach((client, index) => {
      const rowNumber = index + 1;

      // ID validation (if present)
      if (client.id !== undefined && client.id !== null && client.id !== "") {
        // Check for duplicate IDs in CSV
        if (idSet.has(client.id)) {
          errors.push({
            row: rowNumber,
            field: "id",
            message: `Duplicate ID "${client.id}" found in CSV. Each ID must be unique.`,
          });
        } else {
          idSet.add(client.id);
        }

        // Validate ID format (must be positive integer)
        const idNum = Number(client.id);
        if (isNaN(idNum) || idNum < 0 || !Number.isInteger(idNum)) {
          errors.push({
            row: rowNumber,
            field: "id",
            message: "ID must be a positive integer",
          });
        }
      }

      // Required fields validation
      if (!client.firstName || client.firstName.trim() === "") {
        errors.push({
          row: rowNumber,
          field: "firstName",
          message: "First name is required",
        });
      }

      if (!client.lastName || client.lastName.trim() === "") {
        errors.push({
          row: rowNumber,
          field: "lastName",
          message: "Last name is required",
        });
      }

      // Email validation (if provided)
      if (client.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(client.email)) {
        errors.push({
          row: rowNumber,
          field: "email",
          message: "Invalid email format",
        });
      }

      // Phone validation (if provided)
      if (client.phoneNumber) {
        const cleaned = client.phoneNumber.replace(/\D/g, "");
        if (cleaned.length !== 10) {
          errors.push({
            row: rowNumber,
            field: "phoneNumber",
            message: "Phone number must be 10 digits",
          });
        }
      }

      // Type validation
      if (client.type && !["401k", "403b", "federal"].includes(client.type.toLowerCase())) {
        errors.push({
          row: rowNumber,
          field: "type",
          message: "Client type must be 401k, 403b, or federal",
        });
      }

      // LifecycleStage validation (if provided)
      const validLifecycleStages = [
        "prospect",
        "initial_outreach",
        "benefits_analysis",
        "proposal_sent",
        "customer",
        "lost"
      ];
      
      if (client.lifecycleStage && !validLifecycleStages.includes(client.lifecycleStage)) {
        errors.push({
          row: rowNumber,
          field: "lifecycleStage",
          message: `Invalid lifecycle stage "${client.lifecycleStage}". Must be one of: ${validLifecycleStages.join(", ")}`,
        });
      }

      // Related Client ID validation (if provided)
      if (client.related_client_id !== undefined && client.related_client_id !== null && client.related_client_id !== "") {
        const idNum = Number(client.related_client_id);
        if (isNaN(idNum) || idNum < 0 || !Number.isInteger(idNum)) {
          errors.push({
            row: rowNumber,
            field: "related_client_id",
            message: "Related Client ID must be a positive integer",
          });
        }
        // Check if trying to link to self
        if (client.id && idNum.toString() === client.id.toString()) {
          errors.push({
            row: rowNumber,
            field: "related_client_id",
            message: "Client cannot be linked to themselves",
          });
        }
      }
    });

    setValidationErrors(errors);
    return errors;
  };

  const toggleClientSelection = (index: number) => {
    const newSelection = new Set(selectedIndices);
    if (newSelection.has(index)) {
      newSelection.delete(index);
    } else {
      // Only allow selection if client has no validation errors
      const hasError = validationErrors.some(err => err.row === index + 1);
      if (!hasError) {
        newSelection.add(index);
      }
    }
    setSelectedIndices(newSelection);
  };

  const handleSelectAll = () => {
    const validIndices = new Set<number>();
    parsedData.forEach((_, index) => {
      const hasError = validationErrors.some(err => err.row === index + 1);
      if (!hasError) {
        validIndices.add(index);
      }
    });
    setSelectedIndices(validIndices);
  };

  const handleDeselectAll = () => {
    setSelectedIndices(new Set());
  };

  const handleImport = async () => {
    if (selectedIndices.size === 0) {
      toast({
        title: "No Clients Selected",
        description: "Please select at least one client to import.",
        variant: "destructive",
      });
      return;
    }

    if (validationErrors.length > 0) {
      toast({
        title: "Validation Errors",
        description: "Please fix validation errors before importing.",
        variant: "destructive",
      });
      return;
    }

    // Get only selected clients
    const selectedClients = parsedData.filter((_, index) => selectedIndices.has(index));

    // Split into batches
    const batches: ParsedClient[][] = [];
    for (let i = 0; i < selectedClients.length; i += MAX_IMPORT_BATCH) {
      batches.push(selectedClients.slice(i, i + MAX_IMPORT_BATCH));
    }

    // Initialize import state
    setIsImporting(true);
    setCancelImport(false);
    setImportProgress({ current: 0, total: selectedClients.length });
    setImportStats({
      total: selectedClients.length,
      processed: 0,
      successful: 0,
      created: 0,
      updated: 0,
      failed: 0,
      startTime: Date.now(),
    });
    setBatchInfo({
      totalBatches: batches.length,
      currentBatch: 0,
      batchProgress: 0,
      batchSize: 0,
    });

    const failed: FailedClient[] = [];
    let totalSuccessful = 0;
    let totalFailed = 0;

    // Process batches sequentially
    for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
      // Check for cancellation
      if (cancelImport) {
        toast({
          title: "Import Cancelled",
          description: `Import was cancelled after batch ${batchIndex}. ${totalSuccessful} clients were successfully imported.`,
          variant: "default",
        });
        break;
      }

      const batch = batches[batchIndex];
      setBatchInfo({
        totalBatches: batches.length,
        currentBatch: batchIndex + 1,
        batchProgress: 0,
        batchSize: batch.length,
      });

      // Process each client in the batch
      for (let clientIndex = 0; clientIndex < batch.length; clientIndex++) {
        // Check for cancellation within batch
        if (cancelImport) break;

        const client = batch[clientIndex];
        const overallProgress = (batchIndex * MAX_IMPORT_BATCH) + clientIndex + 1;
        
        // Declare clientData outside try block for error logging
        let clientData: any = {};
        let isUpdate = false;

        try {
          // Get current user ID (required for creating clients)
          const userId = api.getUserId();
          if (!userId || userId === 0) {
            throw new Error("User ID is required. Please ensure you are logged in.");
          }

          // Prepare client data for API - ONLY include fields we want to update
          // We explicitly build ONLY the fields we want to send, nothing else
          clientData = {
            firstName: client.firstName,
            lastName: client.lastName,
            type: client.type || "401k",
            user_id: userId, // Required field - must be included for client creation
          };

          // Check if client ID exists and if the client actually exists in the database
          if (client.id !== undefined && client.id !== null && client.id !== "") {
            try {
              // Try to fetch the client to see if it exists
              const existingClient = await api.getClient(client.id);
              if (existingClient && existingClient.id) {
                // Client exists, this is an update
                isUpdate = true;
                // For updates, don't change status if it's not in the CSV
                // This allows users to preserve the current status
              } else {
                // Client doesn't exist, treat as new client
                isUpdate = false;
                clientData.status = "Active";
                // Remove the ID from clientData since we're creating a new client
                console.log(`⚠️ Client ID ${client.id} not found in database. Creating as new client.`);
              }
            } catch (fetchError: any) {
              // If fetching fails (404, etc.), treat as new client
              console.log(`⚠️ Could not fetch client ${client.id}: ${fetchError.message}. Creating as new client.`);
              isUpdate = false;
              clientData.status = "Active";
            }
          } else {
            // No ID provided, this is a new client
            isUpdate = false;
            clientData.status = "Active";
          }

          // Add optional fields ONLY if present
          if (client.email) clientData.email = client.email;
          if (client.phoneNumber) clientData.phoneNumber = client.phoneNumber;
          if (client.dateOfBirth) clientData.dateOfBirth = client.dateOfBirth;
          if (client.stateProvince) clientData.stateProvince = client.stateProvince;
          if (client.lifecycleStage) clientData.lifecycleStage = client.lifecycleStage;
          if (client.related_client_id) clientData.related_client_id = Number(client.related_client_id);
          
          // CRITICAL: Remove any snake_case fields that might have leaked through
          // This prevents sending unexpected fields to the API
          delete (clientData as any).first_name;
          delete (clientData as any).last_name;
          delete (clientData as any).phone_number;
          delete (clientData as any).date_of_birth;
          delete (clientData as any).state_province;
          delete (clientData as any).lifecycle_stage;

          // Call API to create or update client
          if (isUpdate) {
            await api.updateClient(client.id!, clientData as any);
          } else {
            await api.addClient(clientData as any);
          }
          
          totalSuccessful++;
          setImportStats(prev => ({
            ...prev,
            processed: overallProgress,
            successful: prev.successful + 1,
            created: isUpdate ? prev.created : prev.created + 1,
            updated: isUpdate ? prev.updated + 1 : prev.updated,
          }));
        } catch (error: any) {
          totalFailed++;
          
          // Enhanced error logging with full details
          console.error('❌ Import failed for client:', {
            client: `${client.firstName} ${client.lastName}`,
            id: client.id,
            isUpdate,
            dataSent: clientData,
            error: error,
            errorMessage: error.message,
            errorStatus: error.status,
            errorUrl: error.url,
            errorResponse: error.response?.data,
            fullError: JSON.stringify(error, Object.getOwnPropertyNames(error))
          });
          
          // Extract detailed error message
          let errorMsg = "Unknown error";
          
          // Check for structured error response (from apiPost/apiPut)
          if (error.response?.data) {
            const errorData = error.response.data;
            if (errorData.message) {
              errorMsg = errorData.message;
            } else if (errorData.code) {
              if (errorData.code === "ERROR_CODE_NOT_FOUND") {
                errorMsg = `Endpoint not found: ${error.url || 'unknown'}. Status: ${error.status || 'unknown'}. Please verify the API endpoint exists.`;
              } else {
                errorMsg = errorData.code;
              }
              if (errorData.message) {
                errorMsg += `: ${errorData.message}`;
              }
            }
          } else if (error.message) {
            // Use the error message directly (it should already be formatted by apiPost/apiPut)
            errorMsg = error.message;
          }
          
          // If we still have a generic error, include status and URL if available
          if (errorMsg === "Unknown error" && error.status) {
            errorMsg = `API Error ${error.status}: ${error.statusText || 'Unknown error'}`;
            if (error.url) {
              errorMsg += ` (${error.url})`;
            }
          }
          
          failed.push({
            client,
            error: errorMsg,
            batchNumber: batchIndex + 1,
          });
          
          setImportStats(prev => ({
            ...prev,
            processed: overallProgress,
            failed: prev.failed + 1,
          }));
        }

        // Update batch and overall progress
        setBatchInfo(prev => prev ? {
          ...prev,
          batchProgress: clientIndex + 1,
        } : null);
        
        setImportProgress({ 
          current: overallProgress, 
          total: selectedClients.length 
        });
      }

      // Delay between batches (except for the last one)
      if (batchIndex < batches.length - 1 && !cancelImport) {
        await new Promise(resolve => setTimeout(resolve, BATCH_DELAY_MS));
      }
    }

    // Finalize import
    setIsImporting(false);
    setImportComplete(true);
    setImportResults({ success: totalSuccessful, failed: totalFailed });
    setFailedClients(failed);

    // Show results toast
    if (totalSuccessful > 0 && totalFailed === 0) {
      toast({
        title: "🎉 Import Successful!",
        description: `Successfully imported ${totalSuccessful} client(s) in ${batches.length} batch${batches.length !== 1 ? 'es' : ''}.`,
      });
      
      // CRITICAL FIX: Delay onSuccess to allow state updates and cleanup
      // This prevents the beforeunload warning when onSuccess triggers a data refresh
      if (onSuccess) {
        setTimeout(() => {
          onSuccess();
        }, 100);
      }
    } else if (totalSuccessful > 0 && totalFailed > 0) {
      toast({
        title: "Partial Import Completed",
        description: `Imported ${totalSuccessful} client(s). ${totalFailed} failed. View details below.`,
        variant: "default",
      });
    } else {
      toast({
        title: "Import Failed",
        description: `Failed to import clients. View error details below.`,
        variant: "destructive",
      });
    }
  };

  const handleCancelImport = () => {
    setCancelImport(true);
    // Immediately mark as complete to remove beforeunload warning
    setImportComplete(true);
    toast({
      title: "Cancelling Import...",
      description: "Import will stop after the current client is processed.",
    });
  };

  const handleRemoveFile = () => {
    setFile(null);
    setParsedData([]);
    setSelectedIndices(new Set());
    setValidationErrors([]);
    setImportComplete(false);
  };

  const handleStartOver = () => {
    setFile(null);
    setParsedData([]);
    setSelectedIndices(new Set());
    setValidationErrors([]);
    setImportProgress({ current: 0, total: 0 });
    setImportComplete(false);
    setImportResults({ success: 0, failed: 0 });
    setBatchInfo(null);
    setImportStats({
      total: 0,
      processed: 0,
      successful: 0,
      created: 0,
      updated: 0,
      failed: 0,
      startTime: null,
    });
    setFailedClients([]);
    setShowFailedClients(false);
  };

  const downloadTemplate = () => {
    const template = `firstName,lastName,email,phoneNumber,dateOfBirth,stateProvince,type,related_client_id
John,Doe,john.doe@example.com,5551234567,1990-01-15,California,federal,
Jane,Smith,jane.smith@example.com,5559876543,1985-03-22,New York,401k,
Bob,Johnson,bob.johnson@example.com,5555555555,1992-07-08,Texas,403b,`;
    
    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'client_import_template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    toast({
      title: "Template Downloaded",
      description: "Sample CSV template has been downloaded to your computer.",
    });
  };

  // Normalize field names from snake_case to camelCase
  const normalizeFieldNames = (data: ParsedClient[]): ParsedClient[] => {
    return data.map(client => {
      const normalized: ParsedClient = { ...client };
      
      // Map snake_case to camelCase
      // IMPORTANT: ALWAYS prefer snake_case over camelCase if both exist
      // This handles edge case where CSV has BOTH lifecycle_stage and lifecycleStage columns
      
      if (client.first_name !== undefined) {
        normalized.firstName = client.first_name;
        delete normalized.first_name;
      }
      if (client.last_name !== undefined) {
        normalized.lastName = client.last_name;
        delete normalized.last_name;
      }
      if (client.phone_number !== undefined) {
        normalized.phoneNumber = client.phone_number;
        delete normalized.phone_number;
      }
      if (client.date_of_birth !== undefined) {
        normalized.dateOfBirth = client.date_of_birth;
        delete normalized.date_of_birth;
      }
      if (client.state_province !== undefined) {
        normalized.stateProvince = client.state_province;
        delete normalized.state_province;
      }
      
      // CRITICAL FIX: Always prefer lifecycle_stage (snake_case) over lifecycleStage (camelCase)
      // This prevents the bug where CSV has both columns and camelCase has stale data
      if (client.lifecycle_stage !== undefined) {
        // Only set lifecycleStage if lifecycle_stage has a non-empty value
        normalized.lifecycleStage = client.lifecycle_stage || undefined;
        delete normalized.lifecycle_stage;
      }
      
      return normalized;
    });
  };

  // Calculate estimated time remaining
  const calculateTimeRemaining = useCallback(() => {
    if (!importStats.startTime || importStats.processed === 0) {
      return "Calculating...";
    }

    const elapsed = Date.now() - importStats.startTime;
    const avgTimePerClient = elapsed / importStats.processed;
    const remaining = (importStats.total - importStats.processed) * avgTimePerClient;

    const minutes = Math.floor(remaining / 60000);
    const seconds = Math.floor((remaining % 60000) / 1000);

    if (minutes > 0) {
      return `~${minutes}m ${seconds}s`;
    }
    return `~${seconds}s`;
  }, [importStats]);

  // Format processing time
  const formatProcessingTime = useCallback(() => {
    if (!importStats.startTime) return "0s";

    const elapsed = Date.now() - importStats.startTime;
    const minutes = Math.floor(elapsed / 60000);
    const seconds = Math.floor((elapsed % 60000) / 1000);

    if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    }
    return `${seconds}s`;
  }, [importStats.startTime]);

  // Download error report as CSV
  const downloadErrorReport = useCallback(() => {
    if (failedClients.length === 0) return;

    const csv = [
      ['Batch', 'First Name', 'Last Name', 'Email', 'Error'],
      ...failedClients.map(f => [
        f.batchNumber,
        f.client.firstName,
        f.client.lastName,
        f.client.email || '',
        f.error.replace(/,/g, ';') // Replace commas to avoid CSV issues
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `import-errors-${Date.now()}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);

    toast({
      title: "Error Report Downloaded",
      description: "The error report has been saved to your downloads.",
    });
  }, [failedClients, toast]);

  // Calculate if this is a large import
  const isLargeImport = useMemo(() => {
    return parsedData.length > 100;
  }, [parsedData.length]);

  // Calculate total batches for selected clients
  const totalBatches = useMemo(() => {
    return Math.ceil(selectedIndices.size / MAX_IMPORT_BATCH);
  }, [selectedIndices.size]);

  const defaultTrigger = (
    <Button variant="outline" size="sm" className="hover:bg-accent hover:text-accent-foreground transition-all duration-200">
      <Upload className="h-4 w-4 mr-2" />
      Import
    </Button>
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || defaultTrigger}
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-muted border border-accent">
                <FileSpreadsheet className="h-6 w-6 text-accent" />
              </div>
              <div>
                <DialogTitle className="text-2xl">Import Clients</DialogTitle>
                <DialogDescription className="mt-1">
                  Upload a CSV file to import up to {MAX_FILE_CLIENTS.toLocaleString()} clients
                </DialogDescription>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={downloadTemplate}
              className="gap-2 hover:bg-accent hover:text-accent-foreground"
              title="Download a sample CSV template"
            >
              <Download className="h-4 w-4" />
              Template
            </Button>
          </div>
        </DialogHeader>

        <Separator className="my-4" />

        {/* Success State */}
        {importComplete ? (
          <div className="space-y-4">
            {/* Success Header */}
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-accent/20 mb-4">
                <CheckCircle2 className="h-10 w-10 text-accent" />
              </div>
              <h3 className="text-2xl font-bold mb-2">
                Import Complete!
              </h3>
            </div>

            {/* Detailed Stats Card */}
            <div className="p-6 border-2 border-accent bg-muted rounded-lg space-y-4">
              <div className="text-center border-b border-muted-foreground/20 pb-3">
                <p className="text-sm text-muted-foreground mb-1">Import Summary</p>
                <p className="text-3xl font-bold">
                  {importStats.successful} <span className="text-lg text-muted-foreground">of</span> {importStats.total}
                </p>
                <p className="text-sm text-muted-foreground">clients imported successfully</p>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-3 bg-background rounded-lg">
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <div className="w-3 h-3 rounded-full bg-green-500" />
                    <p className="text-2xl font-bold text-green-600">
                      {importStats.created}
                    </p>
                  </div>
                  <p className="text-xs text-muted-foreground">Created</p>
                </div>

                <div className="text-center p-3 bg-background rounded-lg">
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <div className="w-3 h-3 rounded-full bg-blue-500" />
                    <p className="text-2xl font-bold text-blue-600">
                      {importStats.updated}
                    </p>
                  </div>
                  <p className="text-xs text-muted-foreground">Updated</p>
                </div>

                <div className="text-center p-3 bg-background rounded-lg">
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <div className="w-3 h-3 rounded-full bg-destructive" />
                    <p className="text-2xl font-bold text-destructive">
                      {importStats.failed}
                    </p>
                  </div>
                  <p className="text-xs text-muted-foreground">Failed</p>
                </div>

                {batchInfo && batchInfo.totalBatches > 1 ? (
                  <>
                    <div className="text-center p-3 bg-background rounded-lg">
                      <p className="text-2xl font-bold">
                        {batchInfo.totalBatches}
                      </p>
                      <p className="text-xs text-muted-foreground">Batches</p>
                    </div>
                  </>
                ) : (
                  <div className="text-center p-3 bg-background rounded-lg">
                    <div className="flex items-center justify-center gap-1 mb-1">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <p className="text-2xl font-bold">
                        {formatProcessingTime()}
                      </p>
                    </div>
                    <p className="text-xs text-muted-foreground">Time</p>
                  </div>
                )}
              </div>
              
              {/* Processing Time (if multiple batches) */}
              {batchInfo && batchInfo.totalBatches > 1 && (
                <div className="text-center p-3 bg-background rounded-lg border border-muted">
                  <div className="flex items-center justify-center gap-2">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Processing Time:</span>
                    <span className="text-sm font-semibold">{formatProcessingTime()}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Failed Clients Section (if any) */}
            {failedClients.length > 0 && (
              <div className="border border-destructive/30 rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => setShowFailedClients(!showFailedClients)}
                  className="w-full p-3 flex items-center justify-between bg-destructive/10 hover:bg-destructive/20 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-destructive" />
                    <span className="text-sm font-medium">
                      {failedClients.length} Failed Import{failedClients.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <Badge variant="outline" className="text-xs">
                    {showFailedClients ? 'Hide' : 'Show Details'}
                  </Badge>
                </button>

                {showFailedClients && (
                  <div className="p-4 bg-background space-y-2 max-h-64 overflow-y-auto">
                    {failedClients.map((failed, index) => (
                      <div
                        key={index}
                        className="text-sm p-3 bg-destructive/5 border border-destructive/30 rounded"
                      >
                        <div className="flex items-start justify-between mb-1">
                          <span className="font-medium">
                            {failed.client.firstName} {failed.client.lastName}
                          </span>
                          <Badge variant="outline" className="text-xs">
                            Batch {failed.batchNumber}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {failed.client.email || 'No email'}
                        </p>
                        <p className="text-xs text-destructive mt-1">
                          Error: {failed.error}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-center gap-3 pt-2">
              {failedClients.length > 0 && (
                <Button
                  variant="outline"
                  onClick={downloadErrorReport}
                  className="border-destructive text-destructive hover:bg-destructive/10"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download Error Report
                </Button>
              )}
              <Button
                variant="outline"
                onClick={handleStartOver}
              >
                Import More Clients
              </Button>
              <Button
                onClick={() => {
                  setOpen(false);
                  if (onSuccess) onSuccess();
                }}
                className="bg-accent hover:bg-accent"
              >
                Done
              </Button>
            </div>
          </div>
        ) : (
          <>
            {/* File Upload Section */}
            <div className="space-y-4">
              {!file ? (
                // Drag and Drop Area (Empty State)
                <div
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className={`
                    relative border-2 border-dashed rounded-lg p-12 text-center cursor-pointer
                    transition-all duration-200 ease-in-out
                    ${isDragOver 
                      ? 'border-accent bg-muted scale-[1.02]' 
                      : 'border-muted-foreground/25 hover:border-accent hover:bg-muted'
                    }
                    ${isImporting ? 'opacity-50 cursor-not-allowed' : ''}
                  `}
                >
                  <input
                    ref={fileInputRef}
                    id="csv-file"
                    type="file"
                    accept=".csv"
                    onChange={handleFileChange}
                    disabled={isImporting}
                    className="hidden"
                  />
                  <div className="flex flex-col items-center gap-4">
                    <div className="p-4 rounded-full bg-muted border-2 border-accent">
                      <Upload className="h-10 w-10 text-accent" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold mb-1">
                        {isDragOver ? 'Drop your file here' : 'Upload CSV File'}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Drag and drop or click to browse
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center justify-center gap-4 text-xs text-muted-foreground mt-2">
                      <span className="flex items-center gap-1">
                        <FileSpreadsheet className="h-3 w-3" />
                        CSV files only
                      </span>
                      <span>•</span>
                      <span>Max {MAX_FILE_SIZE_MB}MB</span>
                      <span>•</span>
                      <span>Up to {MAX_FILE_CLIENTS.toLocaleString()} clients</span>
                    </div>
                  </div>
                </div>
              ) : (
                // File Info Card
                <div className="space-y-3">
                  <div className="border-2 border-accent bg-muted rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <div className="p-2 rounded-lg bg-accent text-accent-foreground">
                        <FileSpreadsheet className="h-6 w-6" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-sm font-semibold truncate">{file.name}</p>
                          {!isImporting && !isProcessing && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={handleRemoveFile}
                              className="h-8 w-8 hover:bg-destructive/10 hover:text-destructive"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          <span>{(file.size / 1024).toFixed(2)} KB</span>
                          {parsedData.length > 0 && (
                            <>
                              <span>•</span>
                              <Badge variant="secondary" className="text-xs">
                                {parsedData.length} client{parsedData.length !== 1 ? 's' : ''}
                              </Badge>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Large Import Warning */}
                  {isLargeImport && parsedData.length > 0 && (
                    <div className="p-4 bg-accent/10 border border-accent rounded-lg">
                      <div className="flex items-start gap-3">
                        <Info className="h-5 w-5 text-accent mt-0.5 flex-shrink-0" />
                        <div className="text-sm space-y-1">
                          <p className="font-medium">Large Import Detected</p>
                          <p className="text-muted-foreground">
                            This file will be processed in <span className="font-semibold">{totalBatches} batches</span> of up to {MAX_IMPORT_BATCH} clients each.
                            Estimated import time: <span className="font-semibold">~{Math.ceil(parsedData.length * 0.5 / 60)} minutes</span>
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Processing Indicator */}
              {isProcessing && (
                <div className="flex items-center gap-2 p-3 bg-muted border border-accent rounded-lg">
                  <Loader2 className="h-4 w-4 animate-spin text-accent" />
                  <p className="text-sm font-medium">Processing file...</p>
                </div>
              )}

              {/* Validation Errors */}
              {validationErrors.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive rounded-lg">
                    <AlertTriangle className="h-5 w-5 text-destructive" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-destructive">
                        Validation Errors Found ({validationErrors.length})
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Please fix these errors before importing
                      </p>
                    </div>
                  </div>
                  <div className="h-48 border border-muted rounded-lg p-3 overflow-y-auto bg-muted/50">
                    <div className="space-y-2">
                      {validationErrors.map((error, index) => (
                        <div
                          key={index}
                          className="text-sm p-2 bg-background border border-destructive/30 rounded"
                        >
                          <span className="font-medium">Row {error.row}</span> -{" "}
                          <span className="text-muted-foreground">{error.field}:</span>{" "}
                          <span className="text-destructive">{error.message}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

          {/* Client Selection List */}
          {parsedData.length > 0 && !isProcessing && (
            <div className="space-y-3">
              {/* Batch Import Summary (for large imports) */}
              {isLargeImport && selectedIndices.size > 0 && (
                <div className="p-4 bg-muted border border-muted rounded-lg">
                  <div className="flex items-start gap-2 mb-3">
                    <Info className="h-4 w-4 text-muted-foreground mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium mb-1">Batch Import Mode</p>
                      <p className="text-xs text-muted-foreground">
                        Your {selectedIndices.size} selected client{selectedIndices.size !== 1 ? 's' : ''} will be imported in{" "}
                        <span className="font-semibold">{totalBatches} batch{totalBatches !== 1 ? 'es' : ''}</span> of{" "}
                        {MAX_IMPORT_BATCH} clients each. This will take approximately{" "}
                        <span className="font-semibold">{Math.ceil(selectedIndices.size * 0.5 / 60)} minute{Math.ceil(selectedIndices.size * 0.5 / 60) !== 1 ? 's' : ''}</span>.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Selection Header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-medium">
                    Select Clients to Import
                  </h3>
                  <Badge variant={selectedIndices.size > 0 ? "default" : "secondary"}>
                    {selectedIndices.size} / {parsedData.length} selected
                  </Badge>
                </div>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleSelectAll}
                    disabled={isImporting}
                    className="h-8 text-xs"
                  >
                    Select All
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleDeselectAll}
                    disabled={isImporting}
                    className="h-8 text-xs"
                  >
                    Deselect All
                  </Button>
                </div>
              </div>

              {/* Client List */}
              <div className="border rounded-lg max-h-64 overflow-y-auto">
                <div className="divide-y">
                  {parsedData.map((client, index) => {
                    const isSelected = selectedIndices.has(index);
                    const hasError = validationErrors.some(err => err.row === index + 1);
                    const clientErrors = validationErrors.filter(err => err.row === index + 1);
                    
                    return (
                      <div
                        key={index}
                        className={`p-3 hover:bg-muted transition-colors ${
                          hasError ? 'bg-destructive/10' : ''
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          {/* Checkbox */}
                          <Checkbox
                            checked={isSelected}
                            disabled={hasError || isImporting}
                            onCheckedChange={() => toggleClientSelection(index)}
                            className="mt-1"
                          />
                          
                          {/* Client Info */}
                          <div className="flex-1 min-w-0">
                            {/* Header with name and badges */}
                            <div className="flex items-center gap-2 flex-wrap mb-2">
                              <p className="text-sm font-medium">
                                {client.firstName} {client.lastName}
                              </p>
                              <Badge 
                                variant={client.id ? "default" : "outline"} 
                                className={`text-xs ${client.id ? 'bg-blue-500 hover:bg-blue-600' : ''}`}
                              >
                                {client.id ? 'Update' : 'Create'}
                              </Badge>
                              <Badge variant="outline" className="text-xs">
                                {client.type || "401k"}
                              </Badge>
                              <Badge variant="secondary" className="text-xs">
                                Pending
                              </Badge>
                              {hasError && (
                                <Badge variant="destructive" className="text-xs">
                                  <AlertTriangle className="h-3 w-3 mr-1" />
                                  Has Errors
                                </Badge>
                              )}
                            </div>
                            
                            {/* Field Grid - showing all fields */}
                            <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                              {/* Required Fields */}
                              <div className="flex items-start gap-1.5">
                                <span className="text-muted-foreground font-medium min-w-[70px]">First Name:</span>
                                <span className="text-foreground">{client.firstName}</span>
                              </div>
                              <div className="flex items-start gap-1.5">
                                <span className="text-muted-foreground font-medium min-w-[70px]">Last Name:</span>
                                <span className="text-foreground">{client.lastName}</span>
                              </div>
                              
                              {/* Optional Fields */}
                              <div className="flex items-start gap-1.5">
                                <span className="text-muted-foreground font-medium min-w-[70px]">Email:</span>
                                <span className={client.email ? "text-foreground" : "text-muted-foreground italic"}>
                                  {client.email || "Not provided"}
                                </span>
                              </div>
                              <div className="flex items-start gap-1.5">
                                <span className="text-muted-foreground font-medium min-w-[70px]">Phone:</span>
                                <span className={client.phoneNumber ? "text-foreground" : "text-muted-foreground italic"}>
                                  {client.phoneNumber || "Not provided"}
                                </span>
                              </div>
                              <div className="flex items-start gap-1.5">
                                <span className="text-muted-foreground font-medium min-w-[70px]">State:</span>
                                <span className={client.stateProvince ? "text-foreground" : "text-muted-foreground italic"}>
                                  {client.stateProvince || "Not provided"}
                                </span>
                              </div>
                              <div className="flex items-start gap-1.5">
                                <span className="text-muted-foreground font-medium min-w-[70px]">DOB:</span>
                                <span className={client.dateOfBirth ? "text-foreground" : "text-muted-foreground italic"}>
                                  {client.dateOfBirth || "Not provided"}
                                </span>
                              </div>
                            </div>
                            
                            {/* Show errors for this client */}
                            {hasError && (
                              <div className="mt-2 pt-2 border-t border-destructive space-y-1">
                                {clientErrors.map((error, errIdx) => (
                                  <p key={errIdx} className="text-xs text-destructive flex items-start gap-1">
                                    <span className="mt-0.5">•</span>
                                    <span><span className="font-medium">{error.field}:</span> {error.message}</span>
                                  </p>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

              {/* Import Progress */}
              {isImporting && !importComplete && (
                <div className="space-y-4">
                  {/* Main Header */}
                  <div className="text-center">
                    <h3 className="text-lg font-semibold mb-1">
                      Importing/Updating Clients
                    </h3>
                    {batchInfo && batchInfo.totalBatches > 1 && (
                      <p className="text-sm text-muted-foreground">
                        Processing Batch {batchInfo.currentBatch} of {batchInfo.totalBatches}
                      </p>
                    )}
                  </div>

                  {/* Overall Progress Card */}
                  <div className="p-4 border-2 border-accent bg-muted rounded-lg space-y-3">
                    {/* Overall Stats */}
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium">Overall Progress</span>
                      <span className="text-muted-foreground">
                        {importStats.processed} of {importStats.total} clients ({Math.round((importStats.processed / importStats.total) * 100)}%)
                      </span>
                    </div>

                    {/* Overall Progress Bar */}
                    <div className="w-full bg-muted-foreground/20 rounded-full h-4 overflow-hidden">
                      <div
                        className="bg-accent h-4 rounded-full transition-all duration-300 flex items-center justify-end pr-2"
                        style={{
                          width: `${Math.max((importStats.processed / importStats.total) * 100, 2)}%`,
                        }}
                      >
                        {importStats.processed > 0 && (
                          <span className="text-xs font-bold text-accent-foreground">
                            {Math.round((importStats.processed / importStats.total) * 100)}%
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Current Batch Progress (if multiple batches) */}
                    {batchInfo && batchInfo.totalBatches > 1 && (
                      <div className="pt-2 border-t border-muted-foreground/20">
                        <div className="flex items-center justify-between text-xs mb-1.5">
                          <span className="text-muted-foreground">Current Batch</span>
                          <span className="text-muted-foreground">
                            {batchInfo.batchProgress} of {batchInfo.batchSize} clients
                          </span>
                        </div>
                        <div className="w-full bg-muted-foreground/20 rounded-full h-2 overflow-hidden">
                          <div
                            className="bg-accent/60 h-2 rounded-full transition-all duration-300"
                            style={{
                              width: `${(batchInfo.batchProgress / batchInfo.batchSize) * 100}%`,
                            }}
                          />
                        </div>
                      </div>
                    )}

                    {/* Live Stats */}
                    <div className="flex items-center justify-center gap-6 pt-2 border-t border-muted-foreground/20">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-accent animate-pulse" />
                        <span className="text-sm font-medium">
                          {importStats.successful} Successful
                        </span>
                      </div>
                      {importStats.failed > 0 && (
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full bg-destructive" />
                          <span className="text-sm font-medium text-destructive">
                            {importStats.failed} Failed
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Time Estimate */}
                    {importStats.startTime && importStats.processed > 0 && (
                      <div className="text-center text-xs text-muted-foreground pt-2 border-t border-muted-foreground/20">
                        <Clock className="h-3 w-3 inline mr-1" />
                        Estimated time remaining: {calculateTimeRemaining()}
                      </div>
                    )}
                  </div>

                  {/* Cancel Button */}
                  <div className="flex justify-center">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleCancelImport}
                      disabled={cancelImport}
                      className="border-destructive hover:bg-accent hover:text-white"
                    >
                      <XCircle className="h-4 w-4 mr-1" />
                      {cancelImport ? 'Finishing current batch...' : 'Cancel Import'}
                    </Button>
                  </div>
                </div>
              )}

              {/* CSV Format Info - Collapsible */}
              <div className="border border-muted rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => setShowHelp(!showHelp)}
                  className="w-full p-3 flex items-center justify-between bg-muted/30 hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <HelpCircle className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">CSV Format Guide & Tips</span>
                  </div>
                  <Badge variant="outline" className="text-xs">
                    {showHelp ? 'Hide' : 'Show'}
                  </Badge>
                </button>
                
                {showHelp && (
                  <div className="p-4 space-y-3 bg-muted animate-in slide-in-from-top-2">
                    <div>
                      <p className="text-sm font-medium mb-2">Required Columns:</p>
                      <div className="text-xs text-muted-foreground space-y-1 pl-2">
                        <p>• <span className="font-medium text-foreground">firstName</span> or <span className="font-medium text-foreground">first_name</span> <span className="text-destructive">(required)</span></p>
                        <p>• <span className="font-medium text-foreground">lastName</span> or <span className="font-medium text-foreground">last_name</span> <span className="text-destructive">(required)</span></p>
                      </div>
                    </div>
                    
                    <Separator />
                    
                    <div>
                      <p className="text-sm font-medium mb-2">Optional Columns:</p>
                      <div className="text-xs text-muted-foreground space-y-1 pl-2">
                        <p>• <span className="font-medium text-foreground">id</span> - Include to update existing clients</p>
                        <p>• email, phoneNumber (or phone_number)</p>
                        <p>• dateOfBirth (or date_of_birth), stateProvince (or state_province)</p>
                        <p>• type (401k, 403b, federal - defaults to 401k)</p>
                        <p>• lifecycleStage (or lifecycle_stage) - Must be: prospect, initial_outreach, benefits_analysis, proposal_sent, or customer</p>
                        <p>• <span className="font-medium text-foreground">related_client_id</span> - Link spouse to existing client by their ID (must be valid client ID)</p>
                      </div>
                    </div>
                    
                    <Separator />
                    
                    <div className="space-y-2">
                      <p className="text-sm font-medium">Bulk Edit Workflow:</p>
                      <div className="grid grid-cols-1 gap-2 text-xs">
                        <div className="flex items-start gap-2 p-2 bg-blue-500/10 border border-blue-500/20 rounded">
                          <span>✏️</span>
                          <span><span className="font-semibold">Export</span> clients using the export button</span>
                        </div>
                        <div className="flex items-start gap-2 p-2 bg-blue-500/10 border border-blue-500/20 rounded">
                          <span>📊</span>
                          <span><span className="font-semibold">Edit</span> the CSV file in Excel or any spreadsheet app</span>
                        </div>
                        <div className="flex items-start gap-2 p-2 bg-blue-500/10 border border-blue-500/20 rounded">
                          <span>⬆️</span>
                          <span><span className="font-semibold">Re-import</span> to update existing clients (ID column required)</span>
                        </div>
                      </div>
                    </div>
                    
                    <Separator />
                    
                    <div className="space-y-2">
                      <p className="text-sm font-medium">Import Rules:</p>
                      <div className="grid grid-cols-1 gap-2 text-xs">
                        <div className="flex items-start gap-2 p-2 bg-accent/10 border border-accent/20 rounded">
                          <span>💡</span>
                          <span>Both camelCase and snake_case formats accepted</span>
                        </div>
                        <div className="flex items-start gap-2 p-2 bg-muted-foreground/10 border border-muted-foreground/20 rounded">
                          <span>📝</span>
                          <span>All clients imported with <span className="font-semibold">Active</span> status</span>
                        </div>
                        <div className="flex items-start gap-2 p-2 bg-accent/10 border border-accent/20 rounded">
                          <span>🔢</span>
                          <span>Up to <span className="font-semibold">{MAX_FILE_CLIENTS.toLocaleString()} clients</span> per file</span>
                        </div>
                        <div className="flex items-start gap-2 p-2 bg-muted-foreground/10 border border-muted-foreground/20 rounded">
                          <span>📦</span>
                          <span>Large imports processed in batches of {MAX_IMPORT_BATCH}</span>
                        </div>
                        <div className="flex items-start gap-2 p-2 bg-accent/10 border border-accent/20 rounded">
                          <span>⏱️</span>
                          <span>Import time: ~30 seconds per 100 clients</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* Actions */}
        {!importComplete && (
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={isImporting}
              className="hover:bg-accent hover:text-white transition-all duration-200"
            >
              Cancel
            </Button>
            <Button
              onClick={handleImport}
              disabled={
                !file ||
                selectedIndices.size === 0 ||
                isImporting ||
                isProcessing ||
                validationErrors.length > 0
              }
              className="bg-accent hover:bg-accent hover:scale-105 transition-all duration-200 disabled:hover:scale-100"
            >
              {isImporting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Importing...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Import {selectedIndices.size > 0 ? `${selectedIndices.size} Client${selectedIndices.size !== 1 ? 's' : ''}` : ''}
                </>
              )}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

